/**
 * Back-office contract panel — inserted on the candidate profile once
 * their onboarding form is submitted/approved.
 *
 * States and actions map directly to the backend:
 *  - NO CONTRACT YET  → form to prepare a draft (type, region, dates, essai)
 *  - DRAFT / VOIDED   → "Modifier les paramètres", "Aperçu PDF", "Envoyer au candidat"
 *  - SENT / OPENED    → "Aperçu PDF", "Renvoyer l'email", "Copier le lien", "Annuler"
 *  - SIGNED           → "Télécharger contrat signé", audit info (IP, UA, SHA-256, date)
 */
import { motion } from "framer-motion";
import {
    AlertCircle,
    CheckCircle2,
    Clock,
    Copy,
    Download,
    Eye,
    FileSignature,
    FileText,
    Fingerprint,
    Loader2,
    RefreshCw,
    Send,
    Settings2,
    ShieldCheck,
    XCircle,
    Zap,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { useContractOptions } from "../../hooks/useContractOptions";
import { useEmailPreview } from "../../lib/emailPreview";
import { AddContractOptionDialog } from "../AddContractOptionDialog";
import { RenewContractDialog } from "../RenewContractDialog";
import { MarkOfflineSignedModal } from "./MarkOfflineSignedModal";
import RetroactiveContractModal from "./RetroactiveContractModal";
import { computeMaxTrialDays } from "../../lib/trial";
import ContractCorrectionPanel from "./ContractCorrectionPanel";
import EditInfoButton from "./EditInfoButton";

const STATUS_META = {
    draft: { label: "Brouillon", tone: "bg-amber-50 border-amber-200 text-amber-800", Icon: Settings2 },
    sent: { label: "Envoyé au candidat", tone: "bg-sky-50 border-sky-200 text-sky-700", Icon: Send },
    opened: { label: "Ouvert par le candidat", tone: "bg-indigo-50 border-indigo-200 text-indigo-700", Icon: Eye },
    signed: { label: "Contrat signé", tone: "bg-emerald-500 border-emerald-600 text-white", Icon: ShieldCheck },
    archived: { label: "Archivé", tone: "bg-slate-100 border-slate-200 text-slate-700", Icon: FileText },
    voided: { label: "Annulé", tone: "bg-red-50 border-red-200 text-red-700", Icon: XCircle },
};

// NOTE: `ROLE_OPTIONS` and `REGION_OPTIONS` are now loaded dynamically from
// the backend via `useContractOptions()` — they include both built-in and
// user-defined entries. `CONTRACT_TYPE_OPTIONS` stays hardcoded because it
// is tightly coupled to the template branches (partial vs full only).

const CONTRACT_TYPE_OPTIONS = [
    { value: "partial", label: "Temps partiel (24-30h)" },
    { value: "full", label: "Temps plein (35h)" },
];


function todayIsoPlusDays(days) {
    const d = new Date();
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
}

/**
 * Return the last day of the calendar month of a given ISO date (YYYY-MM-DD),
 * as an ISO string. Ex: "2026-07-08" → "2026-07-31", "2026-02-05" → "2026-02-28".
 * The Shariti business rule: all CDDU contracts end on the last day of the
 * start month (month-by-month contracts).
 */
function endOfMonthIso(startIso) {
    if (!startIso) return "";
    const s = new Date(startIso);
    if (isNaN(s)) return "";
    // day 0 of NEXT month = last day of CURRENT month
    const eom = new Date(s.getFullYear(), s.getMonth() + 1, 0);
    const y = eom.getFullYear();
    const m = String(eom.getMonth() + 1).padStart(2, "0");
    const d = String(eom.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
}

/**
 * iter110n · Add N calendar months to an ISO date (YYYY-MM-DD) with
 * end-of-month clamping. Used to derive the CDI trial-end date
 * (3 calendar months by default). Mirrors backend `calendar.monthrange`
 * behaviour in `contract_service.py::_build_context`.
 */
function addMonthsIso(startIso, months) {
    if (!startIso) return "";
    const s = new Date(startIso);
    if (isNaN(s)) return "";
    const y = s.getFullYear();
    const m = s.getMonth();
    const target = new Date(y, m + months, 1);
    // Clamp day to the last day of the target month if the original day
    // is beyond it (e.g. Jan 31 + 1 mois = Feb 28/29).
    const lastDay = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate();
    const day = Math.min(s.getDate(), lastDay);
    const yy = target.getFullYear();
    const mm = String(target.getMonth() + 1).padStart(2, "0");
    const dd = String(day).padStart(2, "0");
    return `${yy}-${mm}-${dd}`;
}

/**
 * Compute the maximum legal trial period (période d'essai) for a CDD /
 * CDDU per Article L1242-10 du Code du travail — see lib/trial.js.
 */

export default function ContractCard({ candidate }) {
    const [loading, setLoading] = useState(true);
    const [contract, setContract] = useState(null);
    const [editing, setEditing] = useState(false);
    // Iter79 · Retro CDI onboarding modal (visible only when no contract yet)
    const [showRetro, setShowRetro] = useState(false);
    // Iter80 · Inline PDF preview URL (auto-refreshed after every regenerate).
    // Contains a blob URL fetched from /pdf-fresh with a cache-buster query.
    // window.open() approach was popup-blocked by Chrome after awaits →
    // moved to an INLINE iframe so the user ALWAYS sees the current PDF
    // right on the page. No confusion possible with stale blob tabs.
    const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
    const [pdfVersion, setPdfVersion] = useState(0);  // counter for user feedback
    const [pdfLoading, setPdfLoading] = useState(false);
    // Iter80 · Live snapshot of onboarding_forms.data (visible above the
    // PDF so admin sees ACTUAL values before/after regenerate — protects
    // against "je ne comprends pas pourquoi rien ne change" perception).
    const [formData, setFormData] = useState(null);
    // Dynamic role + region lists (built-in + user-defined via /contracts/options)
    const {
        roles: roleOptions,
        regions: regionOptions,
        classifications: classificationOptions,
        createRole,
        createRegion,
    } = useContractOptions();
    // Look up a role's `template_base` so we know whether to force full-time.
    const isRoleTreatedAsRE = useCallback((roleKey) => {
        const found = roleOptions.find(r => r.key === roleKey);
        return found?.template_base === "responsable_equipe";
    }, [roleOptions]);
    // Iter65: CD stays contractually full-time. RE became flexible
    // (partial 24-35h possible for team leaders working part-time).
    // Only CD forces the dropdown to full — RE is now user-choice.
    // iter110n · CEO 2026-09-24 · RE Confirmé (CDI) est aussi forcé
    // temps plein (35h) — cf. contract_service.py::is_re_cdi.
    const isRoleForcedFullTime = useCallback((roleKey) => {
        return roleKey === "charge_developpement" || roleKey === "responsable_equipe_confirme";
    }, []);
    // iter110n · CEO 2026-09-24 · Détecte le CDI Responsable d'équipe
    // confirmé — durée indéterminée, salaire mensuel, essai 3 mois,
    // gabarit Jinja dédié (contract_re_cdi_template.html).
    const isRoleCdi = useCallback((roleKey) => {
        return roleKey === "responsable_equipe_confirme";
    }, []);
    // Iter65: monthly-salary pay shape fires only when the role is
    // RE-based (built-in RE, custom RE, or CD) AND the contract is
    // full-time. RE + partial switches to hourly rate (15 €/h default).
    // iter110n · CDI RE Confirmé : toujours salaire mensuel (temps plein).
    const usesMonthlySalary = useCallback((roleKey, contractType) => {
        if (isRoleCdi(roleKey)) return true;
        return isRoleTreatedAsRE(roleKey) && contractType === "full";
    }, [isRoleTreatedAsRE, isRoleCdi]);
    const [params, setParams] = useState(() => {
        // Business rule Shariti (contrats CDDU au mois le mois) :
        // le contrat démarre par défaut demain et finit le dernier jour de ce mois.
        const startD = todayIsoPlusDays(1);
        const endD = endOfMonthIso(startD);
        // iter110n · Pré-remplir le rôle depuis l'attribution posée sur la
        // carte /embauche (`candidate.contract_role`). Fallback historique
        // sur "recruteur_donateurs" si aucune attribution — comportement
        // inchangé pour les candidats déjà en base.
        const initialRole = candidate?.contract_role || "recruteur_donateurs";
        // Seed the hourly rate to match the role default (mirrors backend
        // `default_hourly` in contract_service). Admin can override.
        let initialHourly = 12.5;
        let initialMonthly = 2275.05;
        let initialContractType = "partial";
        let initialTrialDays = Math.max(1, computeMaxTrialDays(startD, endD));
        let initialTrialEndDate = null;
        if (initialRole === "responsable_equipe") initialHourly = 15;
        else if (initialRole === "recruteur_donateurs_confirme") initialHourly = 14;
        else if (initialRole === "charge_developpement") {
            initialContractType = "full";
            initialMonthly = 2578.39;
            initialTrialDays = 2;
        } else if (initialRole === "responsable_equipe_confirme") {
            // iter110n · CDI RE Confirmé — 19 €/h × 151,67h + full-time
            // + essai 3 mois. Le trial_end_date est également posé par
            // l'auto-effect ci-dessous ; on l'initialise ici pour cohérence.
            initialHourly = 19;
            initialMonthly = 2881.73;
            initialContractType = "full";
            initialTrialDays = 90;
            initialTrialEndDate = addMonthsIso(startD, 3);
        }
        return {
            role: initialRole,
            contract_type: initialContractType,
            region: "idf",
            start_date: startD,
            end_date: endD,
            trial_days: initialTrialDays,
            trial_end_date: initialTrialEndDate,
            start_hour: "10",
            hourly_rate_eur: initialHourly,
            monthly_salary_eur: initialMonthly,
            // SYNTEC classification — auto-derived from role (see effect
            // below). Admin can override via the dropdown.
            syntec_position: "1.1",
            syntec_coefficient: "240",
        };
    });
    // Track if the admin has manually picked a classification — once
    // they do, we STOP auto-switching it on role change (respect user
    // choice, per the same pattern as trial_days / end_date overrides).
    const [classifOverridden, setClassifOverridden] = useState(false);
    // Manual overrides — if the admin edits these fields, we stop
    // auto-recomputing them on subsequent changes (respect user choice).
    const [trialOverridden, setTrialOverridden] = useState(false);
    const [endDateOverridden, setEndDateOverridden] = useState(false);
    const [busy, setBusy] = useState(null); // "prepare" | "send" | "void"
    // Void-contract modal state — replaces the old `confirm()` prompt so
    // the CEO can record a proper audit trail (raison) when cancelling a
    // contract for a problematic candidate. Required for legal defence
    // if the candidate later disputes the cancellation.
    const [voidModal, setVoidModal] = useState({ open: false, reason: "" });
    const [preContractDivergence, setPreContractDivergence] = useState(null);
    const [offlineModalOpen, setOfflineModalOpen] = useState(false);
    const emailPreview = useEmailPreview();

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/contracts/candidates/${candidate.id}`);
            setContract(data);
            if (data?.params) {
                setParams({ ...params, ...data.params });
                // CEO 2026-11-01 · Contrat existant : on respecte les valeurs
                // enregistrées (période d'essai, date de fin) — aucun recalcul auto.
                setTrialOverridden(true);
                setEndDateOverridden(true);
            }
        } catch (e) {
            setContract(null);
        } finally {
            setLoading(false);
        }
        // Always refresh the divergence marker — surfaces the mismatch
        // even before the first contract is prepared (so admins spot
        // the typo BEFORE burning it into the PDF).
        try {
            const { data: div } = await api.get(`/contracts/candidates/${candidate.id}/identity-divergence`);
            setPreContractDivergence(div?.divergence || {});
        } catch (e) {
            setPreContractDivergence(null);
        }
        // Iter80 · Fetch the current form.data snapshot so the "Ce qui sera
        // écrit" summary reflects the real onboarding_forms data (proves to
        // the admin that PATCH corrections have landed).
        try {
            const { data: form } = await api.get(`/onboarding/candidates/${candidate.id}`);
            setFormData(form?.data || null);
        } catch (e) {
            setFormData(null);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [candidate.id]);
    useEffect(() => { load(); }, [load]);

    // Iter80 · Cross-component sync — when EditIdentityModal (or any other
    // admin edit UI) saves candidate/form data, it fires
    // `prerec:candidate-data-updated`. We reload our local snapshot so the
    // "Ce qui sera écrit" summary stays LIVE. Fixes the CEO-perceived
    // "je modifie et rien ne bouge" bug (3rd/4th iteration).
    useEffect(() => {
        function handler(e) {
            if (e?.detail?.candidate_id === candidate.id) {
                load();
            }
        }
        window.addEventListener("prerec:candidate-data-updated", handler);
        return () => window.removeEventListener("prerec:candidate-data-updated", handler);
    }, [candidate.id, load]);

    // Iter80 · Fetch a fresh PDF blob for the inline iframe preview.
    // Called on every regenerate + on initial load when a contract exists.
    const refreshPdfPreview = useCallback(async () => {
        try {
            setPdfLoading(true);
            const res = await api.get(
                `/contracts/candidates/${candidate.id}/pdf-fresh`,
                { responseType: "blob", params: { _t: Date.now() } },
            );
            // Revoke the previous blob URL to release memory
            setPdfBlobUrl((prev) => {
                if (prev) {
                    try { URL.revokeObjectURL(prev); } catch (_) { /* noop */ }
                }
                return URL.createObjectURL(res.data);
            });
            setPdfVersion((v) => v + 1);
        } catch (e) {
            // Silent — the "Aperçu PDF" button is the fallback
            setPdfBlobUrl(null);
        } finally {
            setPdfLoading(false);
        }
    }, [candidate.id]);

    // Auto-fetch the PDF preview once we know a contract exists.
    // Depends on contract.id so a NEW contract triggers a refetch.
    useEffect(() => {
        if (contract?.id) {
            refreshPdfPreview();
        } else {
            setPdfBlobUrl((prev) => {
                if (prev) {
                    try { URL.revokeObjectURL(prev); } catch (_) { /* noop */ }
                }
                return null;
            });
        }
        // Cleanup on unmount
        return () => {
            setPdfBlobUrl((prev) => {
                if (prev) {
                    try { URL.revokeObjectURL(prev); } catch (_) { /* noop */ }
                }
                return null;
            });
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contract?.id]);

    // Auto-set end_date to the last day of the start month (Shariti business
    // rule : contrats au mois le mois). Skipped if the admin has manually
    // edited the end date, or if the role is a CDI (durée indéterminée).
    useEffect(() => {
        if (endDateOverridden) return;
        if (isRoleCdi(params.role)) return;
        const auto = endOfMonthIso(params.start_date);
        if (auto && auto !== params.end_date) {
            setParams(p => ({ ...p, end_date: auto }));
        }
    }, [params.start_date, params.end_date, params.role, endDateOverridden, isRoleCdi]);

    // Auto-recompute the legal trial period whenever the dates change,
    // UNLESS the admin has manually edited the value (then we respect their choice).
    // iter110n · Skipped for CDI (uses `trial_end_date` field instead of
    // `trial_days` — 3 mois calendaires par défaut, cf. contract_service).
    useEffect(() => {
        if (trialOverridden) return;
        if (isRoleCdi(params.role)) return;
        const legalMax = computeMaxTrialDays(params.start_date, params.end_date);
        if (legalMax > 0 && legalMax !== params.trial_days) {
            setParams(p => ({ ...p, trial_days: legalMax }));
        }
    }, [params.start_date, params.end_date, params.trial_days, params.role, trialOverridden, isRoleCdi]);

    // iter110n · CDI RE Confirmé · Auto-derive `trial_end_date` = start + 3
    // mois calendaires when the role is CDI and the admin hasn't overridden
    // the trial end date. Mirrors backend `contract_service.py`.
    useEffect(() => {
        if (!isRoleCdi(params.role)) return;
        if (trialOverridden) return;
        const auto = addMonthsIso(params.start_date, 3);
        if (auto && auto !== params.trial_end_date) {
            setParams(p => ({ ...p, trial_end_date: auto }));
        }
    }, [params.start_date, params.role, params.trial_end_date, trialOverridden, isRoleCdi]);

    // Auto-select the default SYNTEC classification when the role
    // changes — unless the admin has explicitly overridden it. Uses
    // `classifications[i].default_for_roles` from the /options/list
    // endpoint so the mapping stays server-owned. Falls back to
    // ETAM 1.1 / 240 if no default is declared for the role.
    useEffect(() => {
        if (classifOverridden) return;
        if (!classificationOptions?.length) return;
        const defaultForRole = classificationOptions.find(
            (c) => (c.default_for_roles || []).includes(params.role),
        );
        const fallback = classificationOptions.find(
            (c) => c.position === "1.1" && c.coefficient === "240",
        ) || classificationOptions[0];
        const target = defaultForRole || fallback;
        if (
            target &&
            (target.position !== params.syntec_position ||
             target.coefficient !== params.syntec_coefficient)
        ) {
            setParams((p) => ({
                ...p,
                syntec_position: target.position,
                syntec_coefficient: target.coefficient,
            }));
        }
    }, [params.role, classificationOptions, classifOverridden, params.syntec_position, params.syntec_coefficient]);

    const legalMaxTrial = useMemo(
        () => computeMaxTrialDays(params.start_date, params.end_date),
        [params.start_date, params.end_date],
    );

    const preparable = useMemo(() => {
        // iter103 (CEO 2026-09-09) : plus de blocage sur trial_days —
        // 0 (pas de période d'essai) est une valeur légitime (ex. CDI
        // avec clause conventionnelle, renouvellement où l'essai est
        // déjà purgé). L'admin garde la main.
        //
        // iter110n · CEO 2026-09-24 · Le CDI Responsable d'équipe confirmé
        // n'a PAS de date de fin (durée indéterminée). On accepte donc
        // `end_date` vide/absente uniquement pour ce rôle. Pour tous les
        // autres contrats (CDDU), la contrainte `end_date > start_date`
        // reste obligatoire.
        const baseOk = (
            params.contract_type && params.region
            && params.start_date
            && params.trial_days >= 0
        );
        if (!baseOk) return false;
        if (isRoleCdi(params.role)) {
            // CDI : end_date optionnel, mais trial_end_date requis (posé
            // par l'auto-effect ou par override manuel).
            return Boolean(params.trial_end_date);
        }
        return Boolean(
            params.end_date && params.start_date < params.end_date
        );
    }, [params, isRoleCdi]);

    async function prepare() {
        if (!preparable) {
            toast.error("Vérifiez les dates et paramètres");
            return;
        }
        setBusy("prepare");
        try {
            const wasRegen = Boolean(contract);
            await api.post(`/contracts/candidates/${candidate.id}/prepare`, params);
            setEditing(false);
            await load();
            // Iter80 · Refresh the inline PDF preview immediately —
            // no window.open (popup-blocked after awaits) and no risk
            // that the admin looks at an old blob tab.
            await refreshPdfPreview();
            if (wasRegen) {
                toast.success("PDF regénéré ✨", {
                    description: "L'aperçu ci-dessous est à jour avec les nouvelles valeurs. Version " + (pdfVersion + 1) + " · " + new Date().toLocaleTimeString(),
                    duration: 6000,
                });
            } else {
                toast.success("Contrat généré ✨", {
                    description: "Aperçu disponible directement dans la carte. Envoyez ensuite au candidat pour signature.",
                    duration: 6000,
                });
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur de génération");
        } finally {
            setBusy(null);
        }
    }

    async function send() {
        // Show the email preview modal FIRST — the recruiter sees the exact
        // signing invite email that will be sent before it fires. The real
        // POST only happens when they click "Envoyer maintenant".
        const performSend = async () => {
            const { data } = await api.post(`/contracts/${contract.id}/send`);
            if (data.provider === "bounced") {
                toast.error("Email rejeté", { description: data.email_error });
            } else {
                toast.success("Email de signature envoyé au candidat ✅");
            }
            await load();
        };
        emailPreview.open({
            template: "contract_signing",
            candidateId: candidate.id,
            contractId: contract.id,
            title: contract.status === "sent" || contract.status === "opened"
                ? "Renvoyer le contrat — aperçu email"
                : "Envoyer le contrat — aperçu email",
            description: "Voici l'email qui sera envoyé au candidat pour signer son contrat. Confirmez pour l'envoyer.",
            confirmLabel: contract.status === "sent" || contract.status === "opened"
                ? "Renvoyer maintenant"
                : "Envoyer maintenant",
            onConfirm: async () => {
                setBusy("send");
                try {
                    await performSend();
                } finally {
                    setBusy(null);
                }
            },
        });
    }

    async function voidContract() {
        // Guard: the reason field is optional at the API level but STRONGLY
        // recommended for audit — the CEO uses this to void contracts of
        // "problematic candidates" (dubitative, red flags) and needs a
        // paper trail. Empty submits are refused inline.
        const reason = (voidModal.reason || "").trim();
        if (!reason || reason.length < 5) {
            toast.error("Précise une raison d'au moins 5 caractères — utile en cas de litige.");
            return;
        }
        setBusy("void");
        try {
            await api.post(`/contracts/${contract.id}/void`, { reason });
            toast.success("Contrat annulé — lien de signature désormais inactif.");
            setVoidModal({ open: false, reason: "" });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Erreur lors de l'annulation");
        } finally {
            setBusy(null);
        }
    }

    async function previewPdf(kind = "preview") {
        try {
            const url = kind === "signed"
                ? `/contracts/${contract.id}/signed.pdf`
                : `/contracts/${contract.id}/preview.pdf`;
            const res = await api.get(url, { responseType: "blob" });
            const blobUrl = URL.createObjectURL(res.data);
            window.open(blobUrl, "_blank", "noopener");
            setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        } catch (e) {
            toast.error("Impossible d'ouvrir le PDF");
        }
    }

    async function copyLink() {
        const base = process.env.REACT_APP_BACKEND_URL.replace(/\/api$/, "");
        const link = `${base}/contract/${contract.token}`;
        try {
            await navigator.clipboard.writeText(link);
            toast.success("Lien copié");
        } catch { /* ignore */ }
    }

    if (loading) {
        return (
            <div className="card-soft p-6 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-shariti-orange" />
            </div>
        );
    }

    // No contract yet — show the prepare form
    if (!contract || editing) {
        return (
            <div className="card-soft p-6 space-y-4" data-testid="contract-card-prepare">
                <IdentityDivergenceBanner divergence={contract?.identity_divergence || preContractDivergence} isSigned={false} />
                <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                        <FileSignature className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-black text-shariti-black">
                            {contract ? "Modifier les paramètres du contrat" : "Générer le contrat"}
                        </h3>
                        <p className="text-sm font-medium text-slate-600 mt-1">
                            Les infos personnelles et l&apos;adresse sont reprises automatiquement du dossier d&apos;embauche.
                        </p>
                        {contract && (
                            <div className="mt-3 flex items-start gap-2 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-[12px] leading-snug text-amber-900" data-testid="contract-regen-notice">
                                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                                <span>
                                    <strong>Important :</strong> les modifications ne sont appliquées au PDF qu&apos;après avoir cliqué
                                    sur <strong>« Régénérer le PDF »</strong>. Si vous cliquez sur « Envoyer » sans régénérer,
                                    l&apos;ancienne version sera envoyée au candidat.
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <Field label="Poste (rôle)">
                        <div className="flex items-center gap-2">
                            <select
                                value={params.role}
                                onChange={(e) => {
                                    const nextRole = e.target.value;
                                    // Iter65: Only CD is contractually full-time.
                                    // RE can now be partial (24-35h) OR full — user
                                    // choice. Also seed role-specific defaults:
                                    //  • CD  → 2578.39 €/mois + 2 j essai
                                    //  • RE  → 2275.05 €/mois (full) + 15 €/h (partial)
                                    //          + trial derived from dates.
                                    //  • RDC → 14 €/h (iter110n · Recruteur confirmé).
                                    //  • RD  → 12.5 €/h (unchanged).
                                    //  • RE Confirmé CDI (iter110n · 2026-09-24) →
                                    //    19 €/h + salaire 2881,73 € + essai 3 mois
                                    //    + temps plein forcé + pas de date de fin.
                                    const next = {
                                        ...params,
                                        role: nextRole,
                                        contract_type: isRoleForcedFullTime(nextRole) ? "full" : params.contract_type,
                                    };
                                    if (nextRole === "charge_developpement") {
                                        next.monthly_salary_eur = 2578.39;
                                        if (!trialOverridden) next.trial_days = 2;
                                    } else if (nextRole === "responsable_equipe_confirme") {
                                        // iter110n · CDI RE Confirmé — 19 €/h
                                        // × 151,67 h = 2 881,73 € brut mensuel
                                        // (recalculé côté backend si non fourni).
                                        next.hourly_rate_eur = 19;
                                        next.monthly_salary_eur = 2881.73;
                                        // CDI = durée indéterminée. On efface
                                        // end_date pour éviter tout contresens
                                        // dans le PDF ou le récap.
                                        next.end_date = null;
                                        // Essai CDI = 3 mois calendaires (auto-derived
                                        // via useEffect ci-dessus). On force le reset
                                        // du flag override pour que l'auto-effect
                                        // recalcule à partir de la nouvelle start_date.
                                        setTrialOverridden(false);
                                        next.trial_end_date = addMonthsIso(next.start_date, 3);
                                        next.trial_days = 90;
                                    } else if (nextRole === "responsable_equipe") {
                                        next.monthly_salary_eur = 2275.05;
                                        // Iter65 — hourly default for RE on partial
                                        // is 15 €/h (CEO decision 2026-07-30). Only
                                        // seed if the admin hasn't already touched it.
                                        if (!params.hourly_rate_eur || params.hourly_rate_eur === 12.5 || params.hourly_rate_eur === 14 || params.hourly_rate_eur === 19) {
                                            next.hourly_rate_eur = 15;
                                        }
                                        if (!trialOverridden) next.trial_days = Math.max(1, computeMaxTrialDays(next.start_date, next.end_date));
                                    } else if (nextRole === "recruteur_donateurs_confirme") {
                                        // iter110n · CEO 2026-09-22 · Recruteur confirmé
                                        // → 14 €/h par défaut. On seed uniquement si
                                        // l'admin n'a pas déjà saisi une valeur custom
                                        // (12.5 = ancien RD, 15 = ancien RE, 19 = ex-CDI).
                                        if (!params.hourly_rate_eur || params.hourly_rate_eur === 12.5 || params.hourly_rate_eur === 15 || params.hourly_rate_eur === 19) {
                                            next.hourly_rate_eur = 14;
                                        }
                                    } else if (nextRole === "recruteur_donateurs") {
                                        // Coming back to RD from a role that
                                        // seeded 14/15/19 €/h — restore the RD default.
                                        if (params.hourly_rate_eur === 15 || params.hourly_rate_eur === 14 || params.hourly_rate_eur === 19) {
                                            next.hourly_rate_eur = 12.5;
                                        }
                                    }
                                    setParams(next);
                                }}
                                data-testid="contract-role"
                                className="input-shariti flex-1"
                            >
                                {roleOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                            </select>
                            <AddContractOptionDialog
                                kind="role"
                                createRole={createRole}
                                createRegion={createRegion}
                                onCreated={(opt) => {
                                    setParams({
                                        ...params,
                                        role: opt.key,
                                        // Custom RE-based roles now default to full-time
                                        // but the admin can toggle to partial (iter65).
                                        contract_type: opt.template_base === "responsable_equipe" ? "full" : params.contract_type,
                                    });
                                }}
                            />
                        </div>
                    </Field>
                    <Field label={isRoleForcedFullTime(params.role) ? "Type de contrat (forcé)" : "Type de contrat"}>
                        <select
                            value={params.contract_type}
                            onChange={(e) => setParams({ ...params, contract_type: e.target.value })}
                            data-testid="contract-type"
                            className="input-shariti"
                            disabled={isRoleForcedFullTime(params.role)}
                            title={isRoleForcedFullTime(params.role) ? "Ce rôle impose un temps plein" : undefined}
                        >
                            {CONTRACT_TYPE_OPTIONS.map(o => {
                                // Iter65: adapt the "Temps partiel" label to
                                // reflect the actual weekly range for the
                                // selected role (RE = 24-35h, RD = 24-30h).
                                // The "Temps plein" label stays 35h whatever
                                // the role.
                                const dynLabel = (o.value === "partial" && isRoleTreatedAsRE(params.role))
                                    ? "Temps partiel (24-35h)"
                                    : o.label;
                                return <option key={o.value} value={o.value}>{dynLabel}</option>;
                            })}
                        </select>
                    </Field>
                    <Field label="Région (métadonnée)">
                        <div className="flex items-center gap-2">
                            <select
                                value={params.region}
                                onChange={(e) => setParams({ ...params, region: e.target.value })}
                                data-testid="contract-region"
                                className="input-shariti flex-1"
                            >
                                {regionOptions.map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
                            </select>
                            <AddContractOptionDialog
                                kind="region"
                                createRole={createRole}
                                createRegion={createRegion}
                                onCreated={(opt) => setParams({ ...params, region: opt.key })}
                            />
                        </div>
                    </Field>
                    <Field label={
                        <span className="inline-flex items-center gap-2">
                            Classification SYNTEC (Article 1)
                            {classifOverridden && (
                                <button
                                    type="button"
                                    onClick={() => setClassifOverridden(false)}
                                    className="text-[10px] text-shariti-orange font-bold underline"
                                    data-testid="reset-classification"
                                    title="Revenir au défaut automatique basé sur le rôle"
                                >
                                    Auto
                                </button>
                            )}
                        </span>
                    }>
                        <select
                            value={`${params.syntec_position}|${params.syntec_coefficient}`}
                            onChange={(e) => {
                                const [pos, coef] = e.target.value.split("|");
                                setParams({ ...params, syntec_position: pos, syntec_coefficient: coef });
                                setClassifOverridden(true);
                            }}
                            data-testid="contract-syntec-classification"
                            title="Position + coefficient de la grille ETAM SYNTEC — apparaît dans l'Article 1 du contrat. Auto-sélectionné selon le rôle, modifiable."
                            className="input-shariti"
                        >
                            {classificationOptions.map((c) => (
                                <option key={c.key} value={`${c.position}|${c.coefficient}`}>
                                    {c.label}
                                </option>
                            ))}
                        </select>
                    </Field>
                    <Field label="Genre pour le contrat">
                        {/* Auto = utilise la civilité de la fiche d'embauche
                            (comportement historique). Homme/Femme force les
                            accords dans le contrat même si la fiche indique
                            autre chose (utile quand la civilité n'a pas été
                            saisie, ou pour générer une variante rapidement). */}
                        <select
                            value={params.civility_override || "auto"}
                            onChange={(e) => setParams({
                                ...params,
                                civility_override: e.target.value === "auto" ? null : e.target.value,
                            })}
                            data-testid="contract-civility-override"
                            className="input-shariti"
                            title="Force le genre affiché sur le contrat — 'Auto' utilise la civilité renseignée sur la fiche d'embauche."
                        >
                            <option value="auto">Auto (selon la fiche)</option>
                            <option value="male">Homme — le salarié</option>
                            <option value="female">Femme — la salariée</option>
                        </select>
                    </Field>
                    <Field label="Date de début">
                        <input type="date" value={params.start_date}
                            onChange={(e) => setParams({ ...params, start_date: e.target.value })}
                            data-testid="contract-start-date"
                            className="input-shariti" />
                    </Field>
                    {isRoleCdi(params.role) ? (
                        <Field label="Durée du contrat">
                            <div
                                data-testid="contract-cdi-duration-info"
                                className="input-shariti flex items-center bg-shariti-cream/60 border-shariti-orange/40 text-shariti-black font-bold text-sm"
                                title="Un CDI est à durée indéterminée — pas de date de fin dans le contrat."
                            >
                                <span className="mr-2 inline-flex items-center justify-center h-5 px-2 rounded-full bg-shariti-orange text-white text-[10px] font-black uppercase tracking-widest">
                                    CDI
                                </span>
                                Durée indéterminée
                            </div>
                        </Field>
                    ) : (
                        <Field label={endDateOverridden ? "Date de fin (manuelle)" : "Date de fin — dernier jour du mois"}>
                            <div className="flex items-center gap-2">
                                <input type="date" value={params.end_date}
                                    onChange={(e) => {
                                        setEndDateOverridden(true);
                                        setParams({ ...params, end_date: e.target.value });
                                    }}
                                    data-testid="contract-end-date"
                                    className="input-shariti flex-1" />
                                {endDateOverridden && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setEndDateOverridden(false);
                                            setParams({ ...params, end_date: endOfMonthIso(params.start_date) });
                                        }}
                                        data-testid="contract-end-date-reset"
                                        className="text-[10px] font-black uppercase tracking-widest text-shariti-orange hover:text-shariti-black transition whitespace-nowrap"
                                        title="Revenir au dernier jour du mois de démarrage (règle Shariti)"
                                    >
                                        Auto
                                    </button>
                                )}
                            </div>
                        </Field>
                    )}
                    <Field label="Heure de prise de poste (H)">
                        <input type="number" min={0} max={23} value={params.start_hour}
                            onChange={(e) => setParams({ ...params, start_hour: e.target.value.padStart(2, "0") })}
                            data-testid="contract-start-hour"
                            className="input-shariti" />
                    </Field>
                    {isRoleCdi(params.role) ? (
                        <Field label={trialOverridden ? "Fin de période d'essai (manuelle)" : "Fin de période d'essai — 3 mois calendaires"}>
                            <div className="flex items-center gap-2">
                                <input type="date" value={params.trial_end_date || ""}
                                    onChange={(e) => {
                                        setTrialOverridden(true);
                                        setParams({ ...params, trial_end_date: e.target.value });
                                    }}
                                    data-testid="contract-cdi-trial-end-date"
                                    title="Période d'essai légale d'un cadre en CDI = 3 mois, renouvelable une fois pour 3 mois max (Art. L1221-19 CT + SYNTEC)."
                                    className="input-shariti flex-1" />
                                {trialOverridden && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTrialOverridden(false);
                                            setParams({ ...params, trial_end_date: addMonthsIso(params.start_date, 3) });
                                        }}
                                        data-testid="contract-cdi-trial-reset"
                                        className="text-[10px] font-black uppercase tracking-widest text-shariti-orange hover:text-shariti-black transition whitespace-nowrap"
                                        title="Recalculer à 3 mois calendaires depuis la date de début (défaut cadre SYNTEC)."
                                    >
                                        Auto
                                    </button>
                                )}
                            </div>
                        </Field>
                    ) : (
                        <Field label={`Période d'essai en jours (0 = aucune · max légal indicatif ${legalMaxTrial} j — L.1242-10)`}>
                            <div className="flex items-center gap-2">
                                <input type="number" min={0} value={params.trial_days}
                                    onChange={(e) => {
                                        setTrialOverridden(true);
                                        // iter103 (CEO 2026-09-09) : aucun blocage
                                        // sur la valeur — l'admin est souverain
                                        // pour définir la période d'essai. Le max
                                        // légal reste affiché à titre d'information
                                        // mais n'empêche jamais la génération.
                                        const raw = e.target.value;
                                        const parsed = raw === "" ? 0 : parseInt(raw, 10);
                                        setParams({ ...params, trial_days: Number.isNaN(parsed) ? 0 : Math.max(0, parsed) });
                                    }}
                                    data-testid="contract-trial-days"
                                    className="input-shariti flex-1" />
                                {trialOverridden && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setTrialOverridden(false);
                                            setParams({ ...params, trial_days: legalMaxTrial });
                                        }}
                                        data-testid="contract-trial-days-reset"
                                        className="text-[10px] font-black uppercase tracking-widest text-shariti-orange hover:text-shariti-black transition whitespace-nowrap"
                                        title={`Recalculer selon L.1242-10 (1 j/semaine, plafond ${legalMaxTrial <= 14 ? "14 j (≤6 mois)" : "30 j (>6 mois)"})`}
                                    >
                                        Auto
                                    </button>
                                )}
                            </div>
                            {params.trial_days > legalMaxTrial && legalMaxTrial > 0 && (
                                <p className="mt-1 text-[10px] font-medium text-slate-500" data-testid="contract-trial-days-warning">
                                    ℹ Au-dessus de l'indicatif L.1242-10 ({legalMaxTrial} j) — valeur acceptée, à confirmer selon la convention collective applicable.
                                </p>
                            )}
                        </Field>
                    )}
                    {usesMonthlySalary(params.role, params.contract_type) ? (
                        <Field label="Salaire mensuel brut (€)">
                            <input type="number" step="0.01" value={params.monthly_salary_eur}
                                onChange={(e) => setParams({ ...params, monthly_salary_eur: parseFloat(e.target.value || 2275.05) })}
                                data-testid="contract-monthly-salary"
                                className="input-shariti" />
                        </Field>
                    ) : (
                        <Field label="Taux horaire brut (€)">
                            <input type="number" step="0.01" value={params.hourly_rate_eur}
                                onChange={(e) => setParams({ ...params, hourly_rate_eur: parseFloat(e.target.value || (isRoleTreatedAsRE(params.role) ? 15 : params.role === "recruteur_donateurs_confirme" ? 14 : 12.5)) })}
                                data-testid="contract-hourly-rate"
                                className="input-shariti" />
                        </Field>
                    )}
                </div>

                <div className="flex flex-wrap items-center gap-2 pt-2">
                    <button
                        onClick={prepare}
                        disabled={!preparable || busy === "prepare"}
                        data-testid="contract-generate"
                        className="inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white h-11 px-5 font-black text-xs uppercase tracking-widest hover:bg-shariti-black transition disabled:opacity-50"
                    >
                        {busy === "prepare" ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileSignature className="h-4 w-4" />}
                        {contract ? "Régénérer le PDF" : "Générer le contrat PDF"}
                    </button>
                    {editing && (
                        <button onClick={() => setEditing(false)} className="text-xs font-bold text-slate-500 hover:text-shariti-black h-11 px-3">
                            Annuler
                        </button>
                    )}
                </div>

                {/* Iter79 · Bouton rétroactif — visible UNIQUEMENT quand il n'y
                    a AUCUN contrat encore (pas en cas d'édition d'un draft) */}
                {!contract && !editing && (
                    <div className="rounded-xl border border-dashed border-shariti-orange/40 bg-shariti-orange-soft/30 p-3 mt-4" data-testid="contract-retro-block">
                        <div className="flex items-start gap-3">
                            <div className="h-8 w-8 rounded-lg bg-shariti-orange text-white flex items-center justify-center shrink-0">
                                <Zap className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-black text-shariti-black">
                                    Contrat déjà signé (CDI / CDD rétroactif) ?
                                </div>
                                <p className="text-[12px] text-slate-600 leading-snug mt-0.5">
                                    Uploadez directement le PDF signé — aucune génération depuis le template.
                                    Le collaborateur passe en <strong>embauché</strong> immédiatement, un matricule
                                    est attribué, et il devient visible dans Booking / Reporting / PayFit.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => setShowRetro(true)}
                                    data-testid="contract-retro-open"
                                    className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-white border border-shariti-orange text-shariti-orange hover:bg-shariti-orange hover:text-white h-8 px-3 text-[11px] font-black uppercase tracking-widest transition"
                                >
                                    <Zap className="h-3 w-3" /> Onboarder rétroactivement
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {showRetro && (
                    <RetroactiveContractModal
                        candidate={candidate}
                        onClose={() => setShowRetro(false)}
                        onDone={async () => {
                            await load();
                        }}
                    />
                )}
            </div>
        );
    }

    const meta = STATUS_META[contract.status] || STATUS_META.draft;
    const Icon = meta.Icon;
    const isSigned = contract.status === "signed";
    const isVoided = contract.status === "voided";
    const canMarkOffline = !isSigned && !isVoided && ["draft", "sent", "opened"].includes(contract.status);

    return (
        <div className="card-soft p-6 space-y-5" data-testid="contract-card">
            {/* Header */}
            <div className="flex items-start gap-4 flex-wrap">
                <div className="h-12 w-12 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                    <FileSignature className="h-5 w-5" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-black text-shariti-black">Contrat de travail</h3>
                    <div className={`mt-1 inline-flex items-center gap-1.5 rounded-full px-3 py-1 border text-[11px] font-bold ${meta.tone}`}>
                        <Icon className="h-3.5 w-3.5" /> {meta.label}
                    </div>
                    <div className="text-[11px] font-medium text-slate-400 mt-1">
                        Généré le {new Date(contract.created_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" })}
                        {contract.sent_at && <> · Envoyé le {new Date(contract.sent_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" })}</>}
                        {contract.signed_at && <> · Signé le {new Date(contract.signed_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "short", timeStyle: "short" })}</>}
                    </div>
                </div>
            </div>

            <IdentityDivergenceBanner divergence={contract.identity_divergence || preContractDivergence} isSigned={isSigned} />

            {/* Params summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 rounded-2xl bg-shariti-cream/60 border border-shariti-gray/40 p-3">
                <ParamTile label="Poste" value={roleOptions.find(o => o.key === (contract.params.role || "recruteur_donateurs"))?.label} />
                <ParamTile label="Type" value={
                    // Iter65: contract_type label depends on ROLE too.
                    //   RE partial → "24-35h" (team leaders part-time)
                    //   RD partial → "24-30h" (existing)
                    //   full       → "35h"
                    // iter110n · CDI RE Confirmé → "CDI · Temps plein 35h"
                    isRoleCdi(contract.params.role || "")
                        ? "CDI · Temps plein (35h)"
                        : contract.params.contract_type === "full"
                            ? "Temps plein (35h)"
                            : (isRoleTreatedAsRE(contract.params.role || "recruteur_donateurs")
                                ? "Temps partiel (24-35h)"
                                : "Temps partiel (24-30h)")
                } />
                <ParamTile label="Région" value={regionOptions.find(o => o.key === contract.params.region)?.label} />
                <ParamTile label="Début" value={contract.params.start_date} />
                {isRoleCdi(contract.params.role || "") ? (
                    <ParamTile label="Fin" value="Durée indéterminée" />
                ) : (
                    <ParamTile label="Fin" value={contract.params.end_date} />
                )}
                {isRoleCdi(contract.params.role || "") ? (
                    <ParamTile
                        label="Essai (fin)"
                        value={contract.params.trial_end_date || "—"}
                    />
                ) : (
                    <ParamTile label="Essai" value={contract.params.trial_days > 0 ? `${contract.params.trial_days} jours` : "Aucune"} />
                )}
                <ParamTile label="Heure début" value={`${contract.params.start_hour} H`} />
                {usesMonthlySalary(contract.params.role || "recruteur_donateurs", contract.params.contract_type) ? (
                    <ParamTile label="Salaire" value={`${contract.params.monthly_salary_eur ?? 2275.05} €/mois`} />
                ) : (
                    <ParamTile label="Taux" value={`${contract.params.hourly_rate_eur} €/h`} />
                )}
                {/* Classification SYNTEC (Article 1) — surfaced in the
                    summary so the CEO can spot at a glance whether the
                    contract will render 2.2/310 for a Responsable etc. */}
                {(contract.params.syntec_position && contract.params.syntec_coefficient) && (
                    <ParamTile
                        label="Classification"
                        value={`ETAM ${contract.params.syntec_position} · coef ${contract.params.syntec_coefficient}`}
                    />
                )}
            </div>

            {/* Iter80 · Live "Ce qui sera écrit dans le contrat" summary.
                Reads onboarding_forms.data.* directly — protects against
                the "je ne comprends pas pourquoi rien ne bouge" perception.
                Values shown here are EXACTLY what render_docx receives on
                the next regenerate. */}
            <FormDataDebugCard
                data={formData}
                candidate={candidate}
                onOpenEdit={() => setEditing(false /* not used but keeps signature */)}
            />

            {/* Iter80 · INLINE PDF PREVIEW — auto-refreshes on every regen
                with a cache-buster. NO MORE window.open (popup-blocked
                after awaits) and NO MORE confusion with stale blob tabs
                (CEO brief 2026-08-03 "les infos ne s'actualisent pas"). */}
            {contract && (
                <div className="rounded-2xl border border-shariti-gray/40 bg-white overflow-hidden" data-testid="contract-inline-pdf">
                    <div className="flex items-center justify-between gap-2 px-3 py-2 bg-shariti-cream/60 border-b border-shariti-gray/40">
                        <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-slate-600">
                            <FileText className="h-3.5 w-3.5 text-shariti-orange" />
                            Aperçu du PDF actuel
                            {pdfVersion > 0 && (
                                <span className="ml-1 text-[9px] font-bold text-slate-400 normal-case tracking-normal" data-testid="contract-pdf-version">
                                    v{pdfVersion} · rafraîchi à {new Date().toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={refreshPdfPreview}
                            disabled={pdfLoading}
                            data-testid="contract-pdf-hard-refresh"
                            title="Force refresh (cache-busted)"
                            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-slate-500 hover:text-shariti-orange disabled:opacity-50"
                        >
                            {pdfLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                            Recharger
                        </button>
                    </div>
                    {pdfLoading && !pdfBlobUrl && (
                        <div className="h-40 flex items-center justify-center text-xs text-slate-500">
                            <Loader2 className="h-4 w-4 animate-spin mr-2" /> Chargement du PDF…
                        </div>
                    )}
                    {pdfBlobUrl && (
                        <iframe
                            key={pdfBlobUrl}
                            src={pdfBlobUrl + "#toolbar=1&navpanes=0&zoom=90"}
                            title={`Contrat SHARITI — ${candidate.first_name} ${candidate.last_name}`}
                            data-testid="contract-inline-pdf-iframe"
                            className="w-full h-[560px] bg-slate-100"
                        />
                    )}
                    {!pdfLoading && !pdfBlobUrl && (
                        <div className="h-40 flex items-center justify-center text-xs text-slate-500 gap-2">
                            <XCircle className="h-4 w-4 text-red-400" />
                            Impossible de charger l&apos;aperçu — vérifiez les paramètres et relancez « Régénérer le PDF ».
                        </div>
                    )}
                </div>
            )}

            {/* Signed audit trail */}
            {isSigned && contract.signature && (
                <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    className="rounded-2xl bg-emerald-50 border border-emerald-200 p-3.5" data-testid="signature-audit">
                    <div className="text-[10px] font-black uppercase tracking-widest text-emerald-800 mb-2 flex items-center gap-1.5">
                        <Fingerprint className="h-3 w-3" />
                        {contract.signature.offline_signed
                            ? "Signature hors ligne — enregistrée par admin"
                            : "Preuve de signature (eIDAS simple)"}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-[12px]">
                        <div><span className="font-semibold text-emerald-800">Signé le : </span><span className="font-bold text-shariti-black">{new Date(contract.signature.signed_at).toLocaleString("fr-FR", { timeZone: "Europe/Paris", dateStyle: "medium", timeStyle: "medium" })}</span></div>
                        {contract.signature.offline_signed ? (
                            <>
                                <div>
                                    <span className="font-semibold text-emerald-800">Enregistré par : </span>
                                    <span className="font-bold text-shariti-black" data-testid="signature-offline-by">
                                        {contract.signature.offline_signed_by_name || "—"}
                                    </span>
                                </div>
                                {contract.signature.offline_note && (
                                    <div className="md:col-span-2 italic text-slate-700">
                                        <span className="font-semibold text-emerald-800 not-italic">Note : </span>
                                        <span data-testid="signature-offline-note">{contract.signature.offline_note}</span>
                                    </div>
                                )}
                                <div className="md:col-span-2">
                                    <span className="font-semibold text-emerald-800">Origine : </span>
                                    <span className="inline-block text-[10px] font-black uppercase tracking-wider text-amber-800 bg-amber-100 border border-amber-300 rounded-full px-2 py-0.5" data-testid="signature-offline-badge">
                                        Signé hors ligne (papier / email)
                                    </span>
                                </div>
                            </>
                        ) : (
                            <>
                                <div><span className="font-semibold text-emerald-800">IP : </span><span className="font-mono text-shariti-black">{contract.signature.ip_address || "—"}</span></div>
                                <div className="md:col-span-2 truncate"><span className="font-semibold text-emerald-800">UA : </span><span className="font-mono text-[10px] text-shariti-black">{contract.signature.user_agent}</span></div>
                            </>
                        )}
                        <div className="md:col-span-2 break-all"><span className="font-semibold text-emerald-800">SHA-256 : </span><span className="font-mono text-[10px] text-shariti-black">{contract.signature.signed_pdf_sha256}</span></div>
                    </div>
                </motion.div>
            )}

            <MarkOfflineSignedModal
                open={offlineModalOpen}
                onClose={() => setOfflineModalOpen(false)}
                contract={contract}
                candidate={candidate}
                onSuccess={() => load()}
            />

            {/* Actions */}
            <div className="flex flex-wrap gap-2 pt-2 border-t border-shariti-gray/50">
                {isSigned ? (
                    <>
                        <button
                            onClick={() => previewPdf("signed")}
                            data-testid="contract-download-signed"
                            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 text-white h-10 px-5 font-black text-xs uppercase tracking-widest hover:bg-emerald-600 transition"
                        >
                            <Download className="h-4 w-4" /> Télécharger le contrat signé
                        </button>
                        <RenewContractDialog contract={contract} onRenewed={load} />
                        <ContractCorrectionPanel contract={contract} onChanged={load} />
                    </>
                ) : (
                    <>
                        <button
                            onClick={() => previewPdf("preview")}
                            data-testid="contract-preview"
                            className="inline-flex items-center gap-2 rounded-full bg-shariti-black text-white h-10 px-4 font-black text-xs uppercase tracking-widest hover:bg-shariti-orange transition"
                        >
                            <Eye className="h-4 w-4" /> Aperçu PDF
                        </button>
                        {!isVoided && (
                            <button
                                onClick={send}
                                disabled={busy === "send"}
                                data-testid="contract-send"
                                className="inline-flex items-center gap-2 rounded-full bg-shariti-orange text-white h-10 px-4 font-black text-xs uppercase tracking-widest hover:bg-shariti-black transition disabled:opacity-60"
                            >
                                {busy === "send" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                                {contract.status === "sent" || contract.status === "opened" ? "Renvoyer l'email" : "Envoyer au candidat"}
                            </button>
                        )}
                        {(contract.status === "sent" || contract.status === "opened") && (
                            <button onClick={copyLink} data-testid="contract-copy-link" className="inline-flex items-center gap-1.5 rounded-full bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 h-10 px-4 text-[11px] font-bold">
                                <Copy className="h-3 w-3" /> Copier le lien
                            </button>
                        )}
                        {canMarkOffline && (
                            <button
                                onClick={() => setOfflineModalOpen(true)}
                                data-testid="contract-mark-offline-signed"
                                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-emerald-300 text-emerald-800 hover:bg-emerald-50 h-10 px-4 text-[11px] font-bold"
                            >
                                <FileSignature className="h-3 w-3" /> Marquer signé hors ligne
                            </button>
                        )}
                        <button
                            onClick={() => setEditing(true)}
                            data-testid="contract-edit"
                            className="inline-flex items-center gap-1.5 rounded-full bg-white border border-shariti-orange text-shariti-orange hover:bg-shariti-orange-soft h-10 px-4 text-[11px] font-bold"
                        >
                            <Settings2 className="h-3 w-3" /> Modifier & régénérer
                        </button>
                        <button
                            onClick={prepare}
                            disabled={busy === "prepare"}
                            data-testid="contract-regenerate-quick"
                            title="Régénère le PDF avec les paramètres actuels — utile après avoir corrigé les infos du candidat (date de naissance, adresse…)."
                            className="inline-flex items-center gap-1.5 rounded-full bg-shariti-orange text-white hover:bg-shariti-black h-10 px-4 text-[11px] font-black uppercase tracking-widest disabled:opacity-60"
                        >
                            {busy === "prepare" ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
                            Régénérer le PDF
                        </button>
                        {!isVoided && (
                            <button
                                onClick={() => setVoidModal({ open: true, reason: "" })}
                                disabled={busy === "void"}
                                data-testid="contract-void"
                                className="inline-flex items-center gap-1.5 rounded-full bg-white border border-red-200 text-red-700 hover:bg-red-50 h-10 px-4 text-[11px] font-bold disabled:opacity-60"
                            >
                                {busy === "void" ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                                Annuler
                            </button>
                        )}
                    </>
                )}
            </div>

            {isVoided && (
                <div className="rounded-2xl bg-red-50 border border-red-200 p-3 flex items-center gap-2 text-sm font-bold text-red-700">
                    <AlertCircle className="h-4 w-4" /> Contrat annulé — cliquez « Modifier & régénérer » pour repartir sur des paramètres corrects.
                </div>
            )}

            {/* Void confirmation modal — a proper dialog (not window.confirm)
                so the CEO can attach a reason that stays on the contract doc
                for later disputes. Layered above the page with a red-tinted
                header to signal a destructive-but-not-catastrophic action. */}
            {voidModal.open && (
                <div
                    className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
                    onClick={() => busy !== "void" && setVoidModal({ open: false, reason: "" })}
                    data-testid="contract-void-modal"
                >
                    <div
                        className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="p-5 bg-red-50 border-b-2 border-red-200">
                            <div className="flex items-center gap-2 text-red-800">
                                <XCircle className="h-5 w-5" />
                                <div className="text-sm font-black uppercase tracking-widest">
                                    Annuler ce contrat
                                </div>
                            </div>
                            <p className="text-xs text-red-900/80 mt-2 leading-relaxed">
                                Le lien de signature du candidat deviendra <strong>immédiatement inactif</strong>.
                                S&apos;il clique dessus il verra une page « Contrat annulé ».
                                Le candidat sera notifié par email, ainsi que les recruteurs / référents.
                                Action <strong>irréversible</strong> — un nouveau contrat devra être régénéré si tu changes d&apos;avis.
                            </p>
                        </div>
                        <div className="p-5 space-y-3">
                            <label className="block">
                                <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">
                                    Raison de l&apos;annulation <span className="text-red-600">*</span>
                                </span>
                                <textarea
                                    value={voidModal.reason}
                                    onChange={(e) => setVoidModal((v) => ({ ...v, reason: e.target.value }))}
                                    placeholder="Ex. Candidat dubitatif — remise en question des termes après entretien. Red flag."
                                    rows={4}
                                    autoFocus
                                    data-testid="contract-void-reason"
                                    className="w-full rounded-lg border-2 border-slate-200 px-3 py-2 text-sm focus:border-red-500 focus:outline-none"
                                />
                                <span className="text-[10px] text-slate-500 mt-1 block">
                                    Cette raison est conservée sur le contrat pour audit. Minimum 5 caractères.
                                </span>
                            </label>
                        </div>
                        <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={() => setVoidModal({ open: false, reason: "" })}
                                disabled={busy === "void"}
                                data-testid="contract-void-cancel"
                                className="px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest text-slate-600 hover:bg-slate-200 disabled:opacity-60"
                            >
                                Non, garder
                            </button>
                            <button
                                type="button"
                                onClick={voidContract}
                                disabled={busy === "void" || (voidModal.reason || "").trim().length < 5}
                                data-testid="contract-void-confirm"
                                className="px-4 py-2 rounded-full text-xs font-black uppercase tracking-widest bg-red-700 text-white hover:bg-red-800 disabled:opacity-60 inline-flex items-center gap-1.5"
                            >
                                {busy === "void" ? <Loader2 className="h-3 w-3 animate-spin" /> : <XCircle className="h-3 w-3" />}
                                {busy === "void" ? "Annulation…" : "Oui, annuler définitivement"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 block">{label}</span>
            {children}
        </label>
    );
}
function ParamTile({ label, value }) {
    return (
        <div className="text-[11px]">
            <div className="font-semibold text-slate-500 uppercase tracking-widest text-[9px] mb-0.5">{label}</div>
            <div className="font-bold text-shariti-black break-words">{value ?? "—"}</div>
        </div>
    );
}

/**
 * IdentityDivergenceBanner — surface a mismatch between the CANDIDATE
 * profile (source of truth, admin-curated to match the pièce d'identité)
 * and the frozen ONBOARDING FORM snapshot. The contract renderer already
 * prefers the candidate value, so the PDF is always correct — this
 * banner is purely informative + gives the admin a direct entry point
 * to fix the typo via the pencil icon on the profile.
 *
 * Hidden when: no contract yet, contract is signed (immutable), no
 * divergence, or divergence prop is missing/empty.
 */
const FIELD_LABELS = {
    civilite: "Civilité",
    first_name: "Prénom",
    last_name: "Nom",
    email: "Email",
};
function IdentityDivergenceBanner({ divergence, isSigned }) {
    if (!divergence || typeof divergence !== "object") return null;
    const entries = Object.entries(divergence);
    if (entries.length === 0) return null;
    if (isSigned) return null; // signed = immutable, banner would be misleading
    return (
        <div
            className="rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3 text-[12px] leading-snug"
            data-testid="contract-identity-divergence"
        >
            <AlertCircle className="h-5 w-5 shrink-0 mt-0.5 text-amber-700" />
            <div className="flex-1 space-y-1.5">
                <div className="font-black text-amber-900 uppercase tracking-widest text-[10px]">
                    Identité fiche candidat ≠ dossier d&apos;embauche
                </div>
                <div className="text-amber-900">
                    Le PDF utilise <strong>la fiche candidat</strong> (source de vérité, doit refléter la pièce d&apos;identité).
                    Le dossier d&apos;embauche conserve ce que le candidat a saisi.
                </div>
                <ul className="mt-1 space-y-0.5 text-amber-900">
                    {entries.map(([key, { candidate: c, form: f }]) => (
                        <li key={key} data-testid={`identity-divergence-${key}`}>
                            <span className="font-semibold">{FIELD_LABELS[key] || key}</span> :
                            <span className="ml-1 font-mono bg-emerald-100 border border-emerald-300 px-1.5 py-0.5 rounded"> {c} </span>
                            <span className="mx-1 text-slate-500">←</span>
                            <span className="font-mono bg-red-50 border border-red-200 px-1.5 py-0.5 rounded line-through text-red-700"> {f} </span>
                        </li>
                    ))}
                </ul>
                <div className="text-[11px] text-amber-800 mt-1.5">
                    Pour corriger la fiche candidat : icône <strong>crayon</strong> à côté du nom en haut de la fiche.
                    Puis clic <strong>« Régénérer le PDF »</strong> pour propager au contrat.
                </div>
            </div>
        </div>
    );
}



// Iter80 · Debug card — shows the LIVE onboarding_forms.data values that
// will be written into the contract PDF. Refreshes on each `load()` of
// the parent card. Primary goal : dispel the CEO's perception that
// "regenerate does not update the PDF" by exposing what the renderer
// actually sees BEFORE regeneration.
function FormDataDebugCard({ data, candidate }) {
    const fields = [
        { key: "birth_date", label: "Date de naissance", render: (v) => v ? formatFrDate(v) : null },
        { key: "birth_city", label: "Ville de naissance" },
        { key: "birth_country", label: "Pays de naissance" },
        { key: "nationality", label: "Nationalité" },
        { key: "social_security_number", label: "N° sécurité sociale" },
        { key: "address_line1", label: "Adresse (rue)" },
        { key: "address_postal_code", label: "Code postal" },
        { key: "address_city", label: "Ville" },
        { key: "iban", label: "IBAN" },
    ];

    const hasData = data && Object.values(data).some(Boolean);
    if (!data && !hasData) {
        return (
            <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="contract-formdata-empty">
                <div className="font-black uppercase tracking-widest text-[10px] mb-1">⚠️ Dossier d&apos;embauche vide</div>
                <div>
                    Aucune donnée dans le dossier d&apos;embauche pour ce candidat. Cliquez sur
                    l&apos;<strong>icône crayon</strong> de la fiche pour saisir date de naissance,
                    adresse, nationalité, etc. Puis <strong>Régénérer le PDF</strong>.
                </div>
            </div>
        );
    }

    const missing = fields.filter(f => !data?.[f.key]);
    const allComplete = missing.length === 0;

    return (
        <div className={`rounded-2xl border p-3 ${allComplete ? "bg-emerald-50 border-emerald-200" : "bg-amber-50 border-amber-200"}`} data-testid="contract-formdata-summary">
            <div className={`flex items-center gap-2 text-[10px] font-black uppercase tracking-widest mb-2 ${allComplete ? "text-emerald-800" : "text-amber-800"}`}>
                <FileText className="h-3 w-3" />
                Ce qui sera écrit dans le contrat
                {!allComplete && (
                    <span className="ml-1 text-amber-700 normal-case tracking-normal font-bold">
                        ({missing.length} champ{missing.length > 1 ? "s" : ""} manquant{missing.length > 1 ? "s" : ""})
                    </span>
                )}
                {allComplete && <span className="ml-1 text-emerald-700 normal-case tracking-normal font-bold">· Dossier complet</span>}
                <EditInfoButton candidateId={candidate.id} testid="contract-formdata-edit" className="ml-auto" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                <FormLine label="Nom légal (contrat)" value={`${candidate.civilite || ""} ${candidate.first_name || "?"} ${candidate.last_name || "?"}`.trim()} filled />
                {fields.map((f) => {
                    const raw = data?.[f.key];
                    const val = f.render ? (raw ? f.render(raw) : null) : raw;
                    return (
                        <FormLine
                            key={f.key}
                            label={f.label}
                            value={val || "—"}
                            filled={Boolean(val)}
                            testid={`formdata-${f.key}`}
                        />
                    );
                })}
            </div>
        </div>
    );
}


function FormLine({ label, value, filled, testid }) {
    return (
        <div className="flex items-baseline gap-2" data-testid={testid}>
            <span className="text-slate-500 shrink-0 min-w-[130px]">{label} :</span>
            <span className={`truncate ${filled ? "font-bold text-shariti-black" : "italic text-amber-600"}`}>
                {value}
            </span>
        </div>
    );
}


function formatFrDate(iso) {
    // yyyy-mm-dd → dd/mm/yyyy
    if (!iso || typeof iso !== "string") return iso;
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return iso;
    return `${m[3]}/${m[2]}/${m[1]}`;
}
